import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { updateProfile } from '../services/authService';
import { usePhAddresses } from '../../../shared/hooks/usePhAddresses';
import { POSITIONS } from '../../../shared/constant/positions';
import { PH_MOBILE_PATTERN, formatPhMobileInput, isValidPhMobile } from '../../../shared/utils/phoneNumber';

function CompleteProfilePage() {
  const navigate = useNavigate();
  const { addresses, loading: addressesLoading } = usePhAddresses();
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [middlename, setMiddlename] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [position, setPosition] = useState('');
  const [province, setProvince] = useState('');
  const [town, setTown] = useState('');
  const [brgy, setBrgy] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const provinces = useMemo(
    () => [...new Set(addresses.map((a) => a.province))].sort(),
    [addresses]
  );

  const towns = useMemo(
    () =>
      province
        ? [...new Set(addresses.filter((a) => a.province === province).map((a) => a.town))].sort()
        : [],
    [addresses, province]
  );

  const barangays = useMemo(
    () =>
      province && town
        ? addresses.filter((a) => a.province === province && a.town === town).map((a) => a.brgy).sort()
        : [],
    [addresses, province, town]
  );

  const handleProvinceChange = (value) => {
    setProvince(value);
    setTown('');
    setBrgy('');
  };

  const handleTownChange = (value) => {
    setTown(value);
    setBrgy('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (contactNumber && !isValidPhMobile(contactNumber)) {
      setError('Contact number must be in the format 09XX-XXX-XXXX.');
      return;
    }

    setLoading(true);
    try {
      await updateProfile({
        firstname,
        lastname,
        middlename: middlename || null,
        contact_number: contactNumber || null,
        position: position || null,
        province: province || null,
        town: town || null,
        brgy: brgy || null,
      });
      navigate('/pending-approval');
    } catch (err) {
      setError(err.message || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-dvh flex items-center justify-center bg-gray-200 px-4 py-8">
      <form onSubmit={handleSubmit} className="w-full max-w-lg bg-white rounded-lg border border-gray-200 p-8 space-y-5">
        <div className="flex flex-col items-center gap-2">
          <img src="/images/cnhs512.png" alt="Logo" className="h-14 w-auto" />
          <p className="text-sm font-semibold text-gray-700 tracking-wide text-center">CALAUAG NHS SPTA PORTAL</p>
        </div>

        <h1 className="text-xl font-semibold text-gray-900 text-center">Complete Your Profile</h1>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label htmlFor="firstname" className="text-sm font-medium text-gray-700">First Name</label>
            <input
              id="firstname"
              type="text"
              required
              value={firstname}
              onChange={(e) => setFirstname(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg focus:outline-none focus:ring-2 focus:ring-gray-400"
            />
          </div>

          <div className="space-y-1">
            <label htmlFor="lastname" className="text-sm font-medium text-gray-700">Last Name</label>
            <input
              id="lastname"
              type="text"
              required
              value={lastname}
              onChange={(e) => setLastname(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg focus:outline-none focus:ring-2 focus:ring-gray-400"
            />
          </div>
        </div>

        <div className="space-y-1">
          <label htmlFor="middlename" className="text-sm font-medium text-gray-700">Middle Name</label>
          <input
            id="middlename"
            type="text"
            value={middlename}
            onChange={(e) => setMiddlename(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg focus:outline-none focus:ring-2 focus:ring-gray-400"
          />
        </div>

        <div className="space-y-1">
          <label htmlFor="contactNumber" className="text-sm font-medium text-gray-700">Contact Number</label>
          <input
            id="contactNumber"
            type="tel"
            inputMode="numeric"
            value={contactNumber}
            onChange={(e) => setContactNumber(formatPhMobileInput(e.target.value))}
            pattern={PH_MOBILE_PATTERN}
            maxLength={13}
            placeholder="09XX-XXX-XXXX"
            title="Contact number must be in the format 09XX-XXX-XXXX"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg focus:outline-none focus:ring-2 focus:ring-gray-400"
          />
        </div>

        <div className="space-y-1">
          <label htmlFor="position" className="text-sm font-medium text-gray-700">Position</label>
          <select
            id="position"
            required
            value={position}
            onChange={(e) => setPosition(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-400"
          >
            <option value="">Select</option>
            {POSITIONS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="space-y-1">
            <label htmlFor="province" className="text-sm font-medium text-gray-700">Province</label>
            <select
              id="province"
              required
              disabled={addressesLoading}
              value={province}
              onChange={(e) => handleProvinceChange(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-400 disabled:opacity-50"
            >
              <option value="">{addressesLoading ? 'Loading...' : 'Select'}</option>
              {provinces.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label htmlFor="town" className="text-sm font-medium text-gray-700">Town</label>
            <select
              id="town"
              required
              disabled={!province}
              value={town}
              onChange={(e) => handleTownChange(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-400 disabled:opacity-50"
            >
              <option value="">Select</option>
              {towns.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label htmlFor="brgy" className="text-sm font-medium text-gray-700">Barangay</label>
            <select
              id="brgy"
              required
              disabled={!town}
              value={brgy}
              onChange={(e) => setBrgy(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-400 disabled:opacity-50"
            >
              <option value="">Select</option>
              {barangays.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-[#3f6d8e] text-white rounded-md py-4 text-sm font-medium hover:bg-[#122436] disabled:opacity-50"
        >
          {loading ? 'Saving...' : 'Continue'}
        </button>
      </form>
    </div>
  );
}

export default CompleteProfilePage;
